// SMS Status across every selected provider.
//
// Each provider schema has its own "SMS" table with its own id sequence, so
// once rows from several schemas are merged, `id` alone collides (STUDENT's
// SMS #42 and public's SMS #42 are different texts). The SMS Status modal keys
// selections, edits and split flags by `msg.id`, so merged rows get a unique
// `id` of "<schema>:<id>" and keep the database id in `_id` and the schema in
// `_schema` (same tag mergeSchemaResults uses). Anything sent back to the
// database or the backend must use smsDbId()/smsSchemaOf(), never `id`.

export const smsKey = (schema, id) => `${schema}:${id}`;

export const tagSmsRows = (schema, rows) =>
  (rows || []).map((row) => ({
    ...row,
    id: row.id === undefined || row.id === null ? row.id : smsKey(schema, row.id),
    _id: row.id,
    _schema: schema,
  }));

export const smsDbId = (row) => (row && row._id !== undefined ? row._id : row?.id);

export const smsSchemaOf = (row, fallback) => row?._schema || fallback || "public";

export const providerSchemas = (selected, homeSchema, fallback) =>
  selected && selected.length ? selected : [homeSchema || fallback || "public"];

export const isFailedSms = (msg) =>
  msg.status === "failed" ||
  msg.status === "undelivered" ||
  Boolean(msg.error_code && msg.error_code !== "None");

export const byCreatedDesc = (a, b) => new Date(b.created_at) - new Date(a.created_at);

// Run `build(<schema's "SMS" query>)` in every schema and merge the tagged
// rows. One provider failing only drops that provider (listed in `failed`);
// every provider failing throws, so callers keep their existing error paths.
export async function querySmsAcrossSchemas(client, schemas, build) {
  const results = await Promise.all(
    schemas.map(async (schema) => {
      try {
        const { data, error } = await build(client.schema(schema).from("SMS"));
        if (error) throw error;
        return { schema, rows: tagSmsRows(schema, data) };
      } catch (error) {
        console.warn(`[SMS Status] provider "${schema}" failed:`, error?.message || error);
        return { schema, rows: null, error };
      }
    }),
  );
  const ok = results.filter((r) => r.rows);
  if (!ok.length && results.length) {
    throw results[0].error || new Error(`Could not load SMS for: ${schemas.join(", ")}`);
  }
  return {
    rows: ok.flatMap((r) => r.rows),
    failed: results.filter((r) => !r.rows).map((r) => r.schema),
  };
}

// Group rows by (schema, key) — a phone number in two providers is two
// different patients' threads and must be categorized against its own
// schema's appointments.
export const groupBySchema = (rows) => {
  const groups = new Map();
  rows.forEach((row) => {
    const schema = smsSchemaOf(row);
    if (!groups.has(schema)) groups.set(schema, []);
    groups.get(schema).push(row);
  });
  return groups;
};
